// Seconds before "send the email again" can be used, after a verification or reset email went out.
export const RESEND_COOLDOWN_SECONDS = 60;

// Key under which signup consent (ISO timestamp) is held until the first verified sign-in.
export const consentKey = (email) => `rigby-consent:${(email || "").trim().toLowerCase()}`;

export function hasPendingConsent(email) {
  try {
    return !!localStorage.getItem(consentKey(email));
  } catch (_) {
    return false;
  }
}

export function clearPendingConsent(email) {
  try {
    localStorage.removeItem(consentKey(email));
  } catch (_) {
    // ignore
  }
}

// True for a Firebase email/password user who hasn't clicked the verification link yet.
export function isUnverifiedPasswordUser(user) {
  return (
    !!user &&
    !user.emailVerified &&
    (user.providerData || []).some((p) => p.providerId === "password")
  );
}

export function validatePassword(password) {
  if (!password) return "";
  const missing = [];
  if (password.length < 12) missing.push("at least 12 characters");
  if (!/[A-Z]/.test(password)) missing.push("one uppercase letter");
  if (!/[a-z]/.test(password)) missing.push("one lowercase letter");
  if (!/[0-9]/.test(password)) missing.push("one number");
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) missing.push("one special character");
  return missing.length ? `Password needs ${missing.join(", ")}` : "";
}

const API_BASE = process.env.REACT_APP_API_URL || "";

// Ask the backend to send the branded verification email. Resolves true when it was sent;
// false means the caller should fall back to Firebase's own email.
export async function requestVerificationEmail(firebaseUser) {
  return (await requestVerificationEmailDetailed(firebaseUser)).status === "sent";
}

// Seconds to wait from a 429's Retry-After header, or undefined when it is missing or not a number.
function retryAfterSeconds(res) {
  const n = Number(res && res.headers && res.headers.get && res.headers.get("Retry-After"));
  return n > 0 ? n : undefined;
}

// Like requestVerificationEmail, but says why it did not send:
//   { status: "sent" } | { status: "rate_limited", retryAfter?: seconds } | { status: "unavailable" }
// "rate_limited" must not fall back to Firebase's own email, which would sidestep our limit.
export async function requestVerificationEmailDetailed(firebaseUser) {
  try {
    const idToken = await firebaseUser.getIdToken(true); // refresh so the display name claim is included
    const res = await fetch(`${API_BASE}/auth/email/verification`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idToken }),
    });
    if (res.status === 429) return { status: "rate_limited", retryAfter: retryAfterSeconds(res) };
    if (!res.ok) return { status: "unavailable" };
    const data = await res.json();
    return data.sent === true ? { status: "sent" } : { status: "unavailable" };
  } catch (_) {
    return { status: "unavailable" };
  }
}

// Tell the backend a reset just finished; it checks with Firebase before sending the security notice.
export async function notifyPasswordChanged(email) {
  try {
    await fetch(`${API_BASE}/auth/email/password-changed`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
  } catch (_) {
    // best effort; the reset itself already succeeded
  }
}

// Same for password reset. The backend answers identically whether or not the account exists.
export async function requestPasswordReset(email) {
  return (await requestPasswordResetDetailed(email)).status === "sent";
}

// Like requestPasswordReset, but says why it did not send:
//   { status: "sent" } | { status: "rate_limited", retryAfter?: seconds } | { status: "unavailable" }
export async function requestPasswordResetDetailed(email) {
  try {
    const res = await fetch(`${API_BASE}/auth/email/password-reset`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (res.status === 429) return { status: "rate_limited", retryAfter: retryAfterSeconds(res) };
    if (!res.ok) return { status: "unavailable" };
    const data = await res.json();
    if (data.rate_limited) {
      const wait = Number(data.retry_after_seconds);
      return { status: "rate_limited", retryAfter: wait > 0 ? wait : undefined };
    }
    return data.sent === true ? { status: "sent" } : { status: "unavailable" };
  } catch (_) {
    return { status: "unavailable" };
  }
}

// The password rules as a checklist, shared by the signup and reset pages.
export function passwordRules(password) {
  const p = password || "";
  return [
    { label: "12+ characters", ok: p.length >= 12 },
    { label: "Uppercase letter", ok: /[A-Z]/.test(p) },
    { label: "Lowercase letter", ok: /[a-z]/.test(p) },
    { label: "Number", ok: /[0-9]/.test(p) },
    { label: "Symbol", ok: /[!@#$%^&*(),.?":{}|<>]/.test(p) },
  ];
}

// Wording for the first-sign-in FERPA consent dialog. It is shared by every sign-in method,
// so only Google accounts are told they are continuing with Google.
export function consentCopy(user) {
  const viaGoogle = !!user && (user.providerData || []).some((p) => p.providerId === "google.com");
  return viaGoogle
    ? { subtitle: "Accept to continue signing in with Google", button: "I Accept – Continue with Google" }
    : { subtitle: "Accept to continue to Rigby", button: "I Accept – Continue" };
}
